import classNames from 'classnames';
import Icon, { IconColor } from '../icon/Icon';
import React, { useCallback, useState } from 'react';

const styles = require('./Alert.module.scss');

type AlertType = 'info' | 'success' | 'warning' | 'error' | 'ai' | 'globe';

const ICON_NAME: Record<AlertType, string> = {
  info: 'info',
  success: 'circleCheck',
  warning: 'circleAlert',
  error: 'triangleAlert',
  ai: 'sparkles',
  globe: 'globe',
};

const ICON_COLOR: Record<AlertType, IconColor> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
  ai: 'ai',
  globe: 'disable',
};

const ALERT_ROLE: Record<AlertType, 'alert' | 'status'> = {
  info: 'status',
  success: 'status',
  warning: 'alert',
  error: 'alert',
  ai: 'status',
  globe: 'status',
};

export interface AlertProps {
  className?: string;
  message: React.ReactNode;
  onClose?: () => void;
  showCloseButton?: boolean;
  type?: AlertType;
}

const Alert: React.FC<AlertProps> = ({
  className,
  message,
  onClose,
  showCloseButton,
  type = 'info',
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    onClose?.();
  }, [onClose]);

  if (!isVisible) return null;

  return (
    <div
      className={classNames(styles.alert, styles[type], className)}
      role={ALERT_ROLE[type]}
    >
      <div className={styles.content}>
        <Icon
          name={ICON_NAME[type]}
          size="md"
          color={ICON_COLOR[type]}
          aria-hidden="true"
        />
        {typeof message === 'string'
          ? <div className={styles.message} dangerouslySetInnerHTML={{ __html: message }} />
          : <div className={styles.message}>{message}</div>
        }
        {showCloseButton && (
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close alert"
            type="button"
          >
            <Icon name="close" size="md" color="black" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Alert;
